/* eslint-disable*/
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  FaCog,
  FaSearch,
  FaRedo,
  FaChevronLeft,
  FaChevronRight,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaFileExport,
} from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import Swal from "sweetalert2";
import { API_URL } from "../config";
import { addMonths, subMonths, startOfMonth } from "date-fns";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import { PermissionWrapper } from "../helpers/permission.js";
import "../components/skeletonRow.css";

// Helper function to parse single range string into min and max values
const parseNumericRange = (value) => {
  if (!value || typeof value !== "string") return { min: undefined, max: undefined };
  const trimmed = value.trim();
  if (!trimmed) return { min: undefined, max: undefined };

  // Match range like "100-200", "100 - 200", "100 to 200"
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)$/i);
  if (rangeMatch) {
    return { min: rangeMatch[1], max: rangeMatch[2] };
  }

  // Match ">=100", ">100", "100+"
  const gteMatch = trimmed.match(/^(?:>=?|>)\s*(\d+(?:\.\d+)?)$/) || trimmed.match(/^(\d+(?:\.\d+)?)\+$/);
  if (gteMatch) {
    return { min: gteMatch[1], max: undefined };
  }

  // Match "<=200", "<200"
  const lteMatch = trimmed.match(/^(?:<=?|<)\s*(\d+(?:\.\d+)?)$/);
  if (lteMatch) {
    return { min: undefined, max: lteMatch[1] };
  }

  // Single number "100": set both min and max for exact match
  const singleMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
  if (singleMatch) {
    return { min: singleMatch[1], max: singleMatch[1] };
  }

  return { min: undefined, max: undefined };
};

const OrderSettlement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const getInitialDateRange = () => {
    const from = queryParams.get("from_date");
    const to = queryParams.get("to_date");
    if (from) {
      return [new Date(from), to ? new Date(to) : null];
    }
    const createdAt = queryParams.get("created_at");
    if (createdAt) {
      if (createdAt.includes(" - ")) {
        const [s, e] = createdAt.split(" - ");
        return [new Date(s.trim()), new Date(e.trim())];
      }
      return [new Date(createdAt), null];
    }
    return [null, null];
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);

  // State for session data
  const [sessionData, setSessionData] = useState({
    from_date: queryParams.get("from_date") || "",
    to_date: queryParams.get("to_date") || "",
    created_at: queryParams.get("created_at") || "",
    sid: queryParams.get("sid") || "",
    amount: queryParams.get("amount") || "",
    status: queryParams.get("status") || "",
    utr_no: queryParams.get("utr_no") || "",
    orderBy: queryParams.get("orderBy") || "created_at",
    orderDir: queryParams.get("orderDir") || "desc",
  });

  // Temporary filter input (so we can type without triggering search)
  const [filterInputs, setFilterInputs] = useState({
    sid: sessionData.sid,
    amount: sessionData.amount,
    status: sessionData.status,
    utr_no: sessionData.utr_no,
  });

  const [orders, setOrders] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDetailsLoading, setExportDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const prevMonthStart = startOfMonth(subMonths(new Date(), 1));

  // 📑 Pagination State
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // Sort state like in your first example
  const [sortField, setSortField] = useState(
    sessionData.orderBy || "created_on"
  );
  const [sortOrder, setSortOrder] = useState(sessionData.orderDir || "desc");

  // Allowed columns for sorting
  const allowedOrderColumns = [
    "created_on",
    "sid",
    "amount",
    "status",
    "utr_no",
  ];

  // Sort function like in your first example
  const sortBox = (field) => {
    if (!allowedOrderColumns.includes(field)) return;

    const newOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortOrder(newOrder);

    const newSessionData = {
      ...sessionData,
      orderBy: field,
      orderDir: newOrder,
    };

    setSessionData(newSessionData);

    const urlParams = new URLSearchParams();
    Object.entries(newSessionData).forEach(([key, value]) => {
      if (value && value !== "") urlParams.set(key, value);
    });
    navigate(`?${urlParams.toString()}`);
  };

  // Get sort icon like in your first example
  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  // Fetch data
  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    const token = localStorage.getItem("authToken");

    try {
      const apiParams = { ...sessionData };

      if (sessionData.amount) {
        const range = parseNumericRange(sessionData.amount);
        if (range.min !== undefined) apiParams.min_amount = range.min;
        if (range.max !== undefined) apiParams.max_amount = range.max;
      }

      Object.keys(apiParams).forEach((key) => {
        if (!apiParams[key]) delete apiParams[key];
      });

      const response = await axios.get(`${API_URL}orderSettlement/list`, {
        params: apiParams,
        headers: { Authorization: `Bearer ${token}` },
      });

      const settlements = response.data || [];
      setOrders(settlements);

      const totalAmount = settlements.reduce(
        (sum, settlement) => sum + parseFloat(settlement.amount || 0),
        0
      );

      setOrderTotal(totalAmount.toFixed(2));
      setTotal(settlements.length);
      setPageCount(Math.ceil(settlements.length / limit));
      setPage(1);
    } catch (error) {
      console.error("Error fetching settlements:", error);
      setError("Failed to fetch settlements data");
      setOrders([]);
      setOrderTotal(0);
      setTotal(0);
      setPageCount(1);
      setPage(1);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [sessionData]);

  useEffect(() => {
    const handleClearFilters = () => {
      handleClear();
    };

    window.addEventListener('clearOrdersSettlementsFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearOrdersSettlementsFiltersEvent', handleClearFilters);
    };
  }, []);

  const hasAnyFilter = () => {
    return (
      dateRange[0] !== null ||
      (filterInputs.sid && filterInputs.sid !== "") ||
      (filterInputs.amount && filterInputs.amount !== "") ||
      (filterInputs.status && filterInputs.status !== "") ||
      (filterInputs.utr_no && filterInputs.utr_no !== "")
    );
  };

  const handleClear = () => {
    setDateRange([null, null]);
    const clearedInputs = {
      sid: "",
      amount: "",
      status: "",
      utr_no: "",
    };
    setFilterInputs(clearedInputs);
    setSessionData({
      from_date: "",
      to_date: "",
      created_at: "",
      ...clearedInputs,
      orderBy: "created_at",
      orderDir: "desc",
    });
    navigate(location.pathname, { replace: true });
  };

  // 📑 Pagination
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const showingRecords = `Showing ${start} to ${end} of ${total} records`;
  const paginatedOrders = orders.slice(start - 1, end);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) setPage(newPage);
  };

  const handleSubmit = () => {
    let from_date = "";
    let to_date = "";
    if (dateRange[0]) {
      from_date = moment(dateRange[0]).format("YYYY-MM-DD");
      to_date = dateRange[1]
        ? moment(dateRange[1]).format("YYYY-MM-DD")
        : from_date;
    }

    const newSessionData = {
      ...sessionData,
      ...filterInputs,
      from_date,
      to_date,
      created_at: "",
    };
    setSessionData(newSessionData);

    const urlParams = new URLSearchParams();
    Object.entries(newSessionData).forEach(([key, value]) => {
      if (value && value !== "") urlParams.set(key, value);
    });
    navigate(`?${urlParams.toString()}`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs({ ...filterInputs, [name]: value });
  };

  // ✅ Trigger search only when pressing Enter
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    const [start, end] = dates;
    if (start && end) {
      const from_date = moment(start).format("YYYY-MM-DD");
      const to_date = moment(end).format("YYYY-MM-DD");
      const newSessionData = {
        ...sessionData,
        from_date,
        to_date,
        created_at: "",
      };
      setSessionData(newSessionData);

      const urlParams = new URLSearchParams();
      Object.entries(newSessionData).forEach(([key, value]) => {
        if (value && value !== "") urlParams.set(key, value);
      });
      navigate(`?${urlParams.toString()}`);
    } else if (!start && !end) {
      const newSessionData = {
        ...sessionData,
        from_date: "",
        to_date: "",
        created_at: "",
      };
      setSessionData(newSessionData);

      const urlParams = new URLSearchParams();
      Object.entries(newSessionData).forEach(([key, value]) => {
        if (value && value !== "") urlParams.set(key, value);
      });
      navigate(`?${urlParams.toString()}`);
    }
  };

  const handleRowClick = (id) => {
    navigate(`/orders/infoSettlements/${id}`);
  };

  const handleExport = async (type) => {
    try {
      if (type === "detail") {
        setExportDetailsLoading(true);
      } else {
        setExportLoading(true);
      }
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams();

      let from_date = sessionData.from_date || "";
      let to_date = sessionData.to_date || "";
      if (dateRange && dateRange[0]) {
        from_date = moment(dateRange[0]).format("YYYY-MM-DD");
        to_date = dateRange[1]
          ? moment(dateRange[1]).format("YYYY-MM-DD")
          : from_date;
      }

      const activeFilters = {
        ...sessionData,
        ...filterInputs,
        from_date,
        to_date,
      };
      delete activeFilters.created_at;

      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, value);
        }
      });

      if (activeFilters.amount) {
        const range = parseNumericRange(activeFilters.amount);
        if (range.min !== undefined) params.set("min_amount", range.min);
        if (range.max !== undefined) params.set("max_amount", range.max);
      }

      const exportUrl =
        type === "detail"
          ? `${API_URL}orderSettlement/exportDetails?${params.toString()}`
          : `${API_URL}orderSettlement/export?${params.toString()}`;

      const response = await axios.get(exportUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `settlements_${type}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
      Swal.fire({
        title: "Export Failed",
        text: "An error occurred while trying to export the data.",
        icon: "error",
      });
    } finally {
      if (type === "detail") {
        setExportDetailsLoading(false);
      } else {
        setExportLoading(false);
      }
    }
  };

  // const formatDate = (dateString) => {
  //   if (!dateString) return "";
  //   const date = new Date(dateString);
  //   return date.toLocaleString("en-IN", {
  //     day: "2-digit",
  //     month: "2-digit",
  //     year: "numeric",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     hour12: true,
  //   });
  // };
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");

    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12; // convert 0 -> 12
    hours = String(hours).padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  return (
    <>
      {isLoading}
      <style>
        {`
          #doublescroll {
            max-height: 580px;
            overflow-y: auto;
            overflow-x: scroll;
          }
          table.bg-white {
            min-height: 400px;
          }
          .table tbody tr {
            height: 50px;
          }
          .custom-datepicker-wrapper .react-datepicker-wrapper,
          .custom-datepicker-wrapper .react-datepicker__input-container {
            width: 100%;
          }
          .custom-datepicker-wrapper input {
            width: 100% !important;
          }
        `}
      </style>
      <div
        id="page-container"
        className="page-header-fixed page-sidebar-fixed fade in"
      >
        <div id="content">
          {error && <div className="alert alert-danger">{error}</div>}
          <div className="row pb-2">
            <div className="col-md-12" style={{ textAlign: "right" }}>
              <div className="d-flex justify-content-end align-items-center gap-2">
                <span style={{ fontSize: "0.8rem" }}>{showingRecords}</span>
                <button
                  className="btn btn-default btn-sm"
                  onClick={fetchData}
                  disabled={isLoading}
                  title="Refresh"
                >
                  <FiRefreshCw />
                </button>

                {hasAnyFilter() && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleClear}
                    title="Clear All Filters"
                  >
                    Clear Filter
                  </button>
                )}

                <PermissionWrapper module="orders_settlements" action="export">
                  <button
                    className="btn btn-default btn-sm"
                    onClick={() => handleExport("detail")}
                    disabled={exportDetailsLoading}
                  >
                    {exportDetailsLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Exporting Detail...
                      </>
                    ) : (
                      <>
                        <FaFileExport /> Export Detail Report
                      </>
                    )}
                  </button>

                  <button
                    className="btn btn-default btn-sm"
                    onClick={() => handleExport("summary")}
                    disabled={exportLoading}
                  >
                    {exportLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <FaFileExport /> Export Report
                      </>
                    )}
                  </button>
                </PermissionWrapper>

                {orders.length > 0 && (
                  <>
                    <button
                      className="btn btn-default btn-sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1 || isLoading}
                      title="Previous"
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      className="btn btn-default btn-sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === pageCount || isLoading}
                      title="Next"
                    >
                      <FaChevronRight />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            className="panel panel-default with-rounded-corner tbl_head_sticky tbl_col_sticky with-shadow p-10"
            id="doublescroll"
          >
            <table className="table table-condensed table-hover bg-white">
              <thead>
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => sortBox("created_at")}
                  >
                    Created {getSortIcon("created_at")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => sortBox("sid")}
                  >
                    Settlement ID {getSortIcon("sid")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => sortBox("amount")}
                  >
                    Amount {getSortIcon("amount")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => sortBox("status")}
                  >
                    Status {getSortIcon("status")}
                  </th>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => sortBox("utr_no")}
                  >
                    UTR No {getSortIcon("utr_no")}
                  </th>
                </tr>

                {/* ✅ Filter Row */}
                <tr>
                  <th style={{ minWidth: "210px" }}>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selectsRange={true}
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                        onChange={handleDateChange}
                        dateFormat="dd-MM-yyyy"
                        className="form-control"
                        placeholderText="Filter date range"
                        isClearable
                        monthsShown={2}
                        openToDate={prevMonthStart || new Date()}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </th>
                  <th>
                    <input
                      type="text"
                      name="sid"
                      value={filterInputs.sid || ""}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                      placeholder="Filter Settlement ID"
                      className="form-control"
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="amount"
                      value={filterInputs.amount || ""}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                      placeholder="e.g. 1000-50000"
                      title="Enter single value or range (e.g. 1000-50000, >=1000, <=50000)"
                      className="form-control"
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="status"
                      value={filterInputs.status || ""}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                      placeholder="Filter Status"
                      className="form-control"
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      name="utr_no"
                      value={filterInputs.utr_no || ""}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      disabled={isLoading}
                      placeholder="Filter UTR No"
                      className="form-control"
                    />
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  Array.from({ length: 10 }).map((_, skeletonIndex) => (
                    <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                      {Array.from({ length: 5 }).map((_, colIndex) => (
                        <td key={colIndex}>
                          <div
                            className="skeleton-text"
                            style={{ width: colIndex === 0 ? "80%" : "50%", margin: colIndex === 0 ? "0" : "0 auto" }}
                          ></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedOrders.length > 0 ? (
                  paginatedOrders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => handleRowClick(order.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{formatDate(order.created_at)}</td>
                      <td>{order.sid}</td>
                      <td style={{ textAlign: "center" }}>
                        {parseFloat(order.amount || 0).toFixed(2)}
                      </td>
                      <td>
                        <span
                          className={`status-badge ${order.status?.toLowerCase()}`}
                        >
                          {order.status}
                        </span>
                      </td>
                      <td>{order.utr_no}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan="2">{showingRecords}</td>
                  <td style={{ textAlign: "center", fontWeight: "bold" }}>
                    {orderTotal}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderSettlement;
